import { Link } from 'react-router-dom'
import LegalPageLayout, {
  LegalAddress,
  LegalList,
  LegalSection,
  LegalSubheading,
} from '../../../components/legal/LegalPageLayout'
import {
  LEGAL_EFFECTIVE_DATE,
  LEGAL_OPERATOR_NAME,
  LEGAL_PRIVACY_OFFICER_NAME,
  LEGAL_SITE_TYPE,
} from '../../../content/legalEntity'
import { usePublicSiteBranding } from '../../../contexts/PublicSiteContext'

export default function PrivacyPolicyPage() {
  const branding = usePublicSiteBranding()
  const siteName = branding.productDisplayName

  return (
    <LegalPageLayout title="Privacy Policy">
      <p className="text-sm font-medium uppercase tracking-wide text-primary">
        {siteName}
      </p>
      <h1 className="mt-2 text-3xl font-extrabold text-marketing-ink">Privacy Policy</h1>
      <p className="mt-3 text-sm text-gray-600">
        Type of website: {LEGAL_SITE_TYPE}
        <br />
        Effective date: {LEGAL_EFFECTIVE_DATE}
      </p>
      <p className="mt-6 text-base leading-7 text-gray-800">
        {siteName} (the &quot;Site&quot;) is owned and operated by {LEGAL_OPERATOR_NAME}.{' '}
        {LEGAL_OPERATOR_NAME} can be contacted at:
      </p>
      <div className="mt-3 text-base leading-7 text-gray-800">
        <LegalAddress />
      </div>

      <LegalSection title="Purpose">
        <p>
          The purpose of this privacy policy (this &quot;Privacy Policy&quot;) is to inform
          users of our Site of the following:
        </p>
        <LegalList
          items={[
            'The personal data we will collect;',
            'Use of collected data;',
            'Who has access to the data collected;',
            'The rights of Site users; and',
            "The Site's cookie policy.",
          ]}
        />
        <p>
          This Privacy Policy applies in addition to the{' '}
          <Link to="/terms-of-service" className="text-primary underline hover:text-primary-dark">
            terms and conditions
          </Link>{' '}
          of our Site.
        </p>
      </LegalSection>

      <LegalSection title="Consent">
        <p>By using our Site users agree that they consent to:</p>
        <LegalList
          items={[
            'The conditions set out in this Privacy Policy; and',
            'The collection, use, and retention of the data listed in this Privacy Policy.',
          ]}
        />
      </LegalSection>

      <LegalSection title="Personal Data We Collect">
        <p>
          We only collect data that helps us achieve the purpose set out in this Privacy
          Policy. We will not collect any additional data beyond the data listed below
          without notifying you first.
        </p>
        <LegalSubheading>Data Collected Automatically</LegalSubheading>
        <p>When you visit and use our Site, we may automatically collect and store the following information:</p>
        <LegalList items={['Location.']} />
        <LegalSubheading>Data Collected in a Non-Automatic Way</LegalSubheading>
        <p>We may also collect the following data when you perform certain functions on our Site:</p>
        <LegalList items={['First and last name; and', 'Email address.']} />
        <p>This data may be collected using the following methods:</p>
        <LegalList items={['Creating an account.']} />
      </LegalSection>

      <LegalSection title="How We Use Personal Data">
        <p>
          Data collected on our Site will only be used for the purposes specified in this
          Privacy Policy or indicated on the relevant pages of our Site. We will not use
          your data beyond what we disclose in this Privacy Policy.
        </p>
        <p>The data we collect automatically is used for the following purposes:</p>
        <LegalList items={['Reporting security walks of storage facilities.']} />
        <p>The data we collect when the user performs certain functions may be used for the following purposes:</p>
        <LegalList items={['Communication.']} />
      </LegalSection>

      <LegalSection title="Who We Share Personal Data With">
        <LegalSubheading>Employees</LegalSubheading>
        <p>
          We may disclose user data to any member of our organization who reasonably needs
          access to user data to achieve the purposes set out in this Privacy Policy.
        </p>
        <LegalSubheading>Third Parties</LegalSubheading>
        <p>We may share user data with the following third parties:</p>
        <LegalList items={['Companies that register with us.']} />
        <p>We may share the following user data with third parties:</p>
        <LegalList items={['Location data for walks.']} />
        <p>We may share user data with third parties for the following purposes:</p>
        <LegalList items={['Reporting on timing and fullness of facility walks.']} />
        <p>
          Third parties will not be able to access user data beyond what is reasonably
          necessary to achieve the given purpose.
        </p>
        <LegalSubheading>Other Disclosures</LegalSubheading>
        <p>We will not sell or share your data with other third parties, except in the following cases:</p>
        <LegalList
          items={[
            'If the law requires it;',
            'If it is required for any legal proceeding;',
            'To prove or protect our legal rights; and',
            'To buyers or potential buyers of this company in the event that we seek to sell the company.',
          ]}
        />
        <p>
          If you follow hyperlinks from our Site to another Site, please note that we are
          not responsible for and have no control over their privacy policies and practices.
        </p>
      </LegalSection>

      <LegalSection title="How Long We Store Personal Data">
        <p>User data will be stored until the purpose the data was collected for has been achieved.</p>
        <p>You will be notified if your data is kept for longer than this period.</p>
      </LegalSection>

      <LegalSection title="How We Protect Your Personal Data">
        <p>
          In order to protect your security, we use the strongest available browser
          encryption and store all of our data on servers in secure facilities. All data is
          only accessible to our employees. Our employees are bound by strict confidentiality
          agreements and a breach of this agreement would result in the employee&apos;s
          termination.
        </p>
        <p>
          While we take all reasonable precautions to ensure that user data is secure and
          that users are protected, there always remains the risk of harm. The Internet as
          a whole can be insecure at times and therefore we are unable to guarantee the
          security of user data beyond what is reasonably practical.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          We do not knowingly collect or use personal data from children under 13 years of
          age. If we learn that we have collected personal data from a child under 13 years
          of age, the personal data will be deleted as soon as possible. If a child under 13
          years of age has provided us with personal data their parent or guardian may
          contact our privacy officer.
        </p>
      </LegalSection>

      <LegalSection title="How to Access, Modify, Delete, or Challenge the Data Collected">
        <p>
          If you would like to know if we have collected your personal data, how we have
          used your personal data, if we have disclosed your personal data and to who we
          disclosed your personal data, or if you would like your data to be deleted or
          modified in any way, please contact our privacy officer, {LEGAL_PRIVACY_OFFICER_NAME}:
        </p>
        <LegalAddress />
      </LegalSection>

      <LegalSection title="Do Not Track Notice">
        <p>
          Do Not Track (&quot;DNT&quot;) is a privacy preference that you can set in certain
          web browsers. We do not track the users of our Site over time and across third
          party websites and therefore do not respond to browser-initiated DNT signals. We
          are not responsible for and cannot guarantee how any third parties who interact
          with our Site and your data will respond to DNT signals.
        </p>
      </LegalSection>

      <LegalSection title="Cookie Policy">
        <p>
          A cookie is a small file, stored on a user&apos;s hard drive by a website. Its
          purpose is to collect data relating to the user&apos;s browsing habits. You can
          choose to be notified each time a cookie is transmitted. You can also choose to
          disable cookies entirely in your internet browser, but this may decrease the
          quality of your user experience.
        </p>
        <p>We use the following types of cookies on our Site:</p>
        <LegalList
          items={[
            <>
              <span className="font-semibold">Functional cookies</span>
              <br />
              Functional cookies are used to remember the selections you make on our Site so
              that your selections are saved for your next visits.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="Modifications">
        <p>
          This Privacy Policy may be amended from time to time in order to maintain
          compliance with the law and to reflect any changes to our data collection process.
          When we amend this Privacy Policy we will update the &quot;Effective Date&quot; at
          the top of this Privacy Policy. We recommend that our users periodically review
          our Privacy Policy to ensure that they are notified of any updates. If necessary,
          we may notify users by email of changes to this Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection title="Contact Information">
        <p>
          If you have any questions, concerns or complaints, you can contact our privacy
          officer, {LEGAL_PRIVACY_OFFICER_NAME}, at:
        </p>
        <LegalAddress />
      </LegalSection>
    </LegalPageLayout>
  )
}
